import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Check, Loader, Users, User } from 'lucide-react';
import { clusterApiUrl, Connection, PublicKey, SystemProgram } from '@solana/web3.js';

import { program_address, vault_pda, server_url, chain } from '../utils/Constant';
import { AnchorProvider, BN, Program } from '@coral-xyz/anchor';
import { IDL } from '@/utils/Tornado';
import { useWallet } from '@solana/wallet-adapter-react';
import axios from 'axios';

import { Buffer } from 'buffer';

window.Buffer = Buffer;

type Status = 'pending' | 'processing' | 'complete';

interface Step {
  label: string;
  status: Status;
}

interface TransactionProgressProps {
  isMulti?: boolean;
  destinationCount?: number;
  data: any;
}



const TransactionProgress = ({ isMulti = false, destinationCount = 1, data = {} }: TransactionProgressProps) => {

  const wallet = useWallet();
  const connection = new Connection(clusterApiUrl(chain));
  const PROGRAM_ID = new PublicKey(program_address);
  const VAULT_SEED = vault_pda;
  const provider = new AnchorProvider(connection, wallet, {});

  const navigate = useNavigate();
  console.log(`TransactionProgress data::`, data);

  const [steps, setSteps] = useState<Step[]>([
    { label: 'Receiving funds', status: 'pending' },
    { label: 'Splitting transaction', status: 'pending' },
    { label: 'Routing through privacy pools', status: 'pending' },
    { label: isMulti ? `Distributing to ${destinationCount} destinations` : 'Sending to destination', status: 'pending' }
  ]);

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {

    (async () => {

      setSteps(prev => {
        const updated = [...prev];
        updated[0].status = 'processing';
        return updated;
      });

      const VAULT_PDA = PublicKey.findProgramAddressSync(
        [Buffer.from(VAULT_SEED)],
        PROGRAM_ID
      )[0];

      console.log(`vault_pda::`, VAULT_PDA.toBase58());
      console.log(`wallet.publicKey::`, wallet.publicKey);

      const program = new Program(IDL, PROGRAM_ID, provider);

      const txSig = await program.methods
        .deposit(new BN(Number(data.amount) * 1e9))
        .accounts({
          depositor: wallet.publicKey,
          vault: VAULT_PDA,
          systemProgram: SystemProgram.programId
        })
        .rpc();
      console.log(`txSig::`, txSig);

      setSteps(prev => {
        const updated = [...prev];
        updated[0].status = 'complete';
        updated[1].status = 'processing';
        return updated;
      });
      setCurrentStep(1);

      if (!data.isMulti) {

        let res = await axios.post(`${server_url}/withdraw`, {
          amount: data.amount, destination: data.destinationAddress
        });

        console.log(`result::`, res.data);

        setSteps(prev => {
          const updated = [...prev];
          updated[1].status = 'complete';
          updated[2].status = 'processing';
          return updated;
        });

        setCurrentStep(2);

        if (res.data.result) {
          const timer1 = setTimeout(() => {
            setSteps(prev => {
              const updated = [...prev];
              updated[2].status = 'complete';
              updated[3].status = 'processing';
              return updated;
            });
            setCurrentStep(3);
          }, 2000);

          const timer2 = setTimeout(() => {
            setSteps(prev => {
              const updated = [...prev];
              updated[3].status = 'complete';
              return updated;
            });

          }, 3500);

          const timer3 = setTimeout(() => {
            // Navigate to confirm page with the same state data
            navigate('/confirm', {
              state: {
                ...data,
                deposit: txSig
              }
            });
          }, 4000)

          return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
          };
        }

      } else {

        let res = await axios.post(`${server_url}/withdraw-multi`, {
          multiDestinations: data.multiDestinations
        });
        console.log(`result::`, res.data);

        setSteps(prev => {
          const updated = [...prev];
          updated[1].status = 'complete';
          updated[2].status = 'processing';
          return updated;
        });
        setCurrentStep(2);

        if (res.data.result) {
          const timer1 = setTimeout(() => {
            setSteps(prev => {
              const updated = [...prev];
              updated[2].status = 'complete';
              updated[3].status = 'processing';
              return updated;
            });
            setCurrentStep(3);
          }, 2000);

          const timer2 = setTimeout(() => {
            setSteps(prev => {
              const updated = [...prev];
              updated[3].status = 'complete';
              return updated;
            });


          }, 3500);

          const timer3 = setTimeout(() => {
            // Navigate to confirm page with the same state data
            navigate('/confirm', {
              state: {
                ...data,
                deposit: txSig
              }
            });
          }, 4000)

          return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
          };
        }
      }
    })()

  }, [destinationCount, isMulti, navigate, data]);

  // Calculate progress percentage with better precision
  const progressPercentage = Math.min(Math.round((currentStep / (steps.length - 1)) * 100), 100);

  return (
    <div className="space-y-8 max-w-lg mx-auto">
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 h-full w-0.5 bg-muted">
          <div
            className="w-0.5 bg-gradient-to-b from-whirlpool-cyan to-whirlpool-blue transition-all duration-700 ease-in-out shadow-glow"
            style={{ height: `${progressPercentage}%` }}
          ></div>
        </div>

        {steps.map((step, index) => (
          <div key={index} className="flex items-start mb-6 relative z-10">
            <div className="flex items-center justify-center">
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full ${step.status === 'complete'
                  ? 'bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue text-white shadow-glow'
                  : step.status === 'processing'
                    ? 'bg-secondary border border-whirlpool-cyan shadow-glow'
                    : 'bg-secondary'
                  }`}
              >
                {step.status === 'complete' && <Check className="w-5 h-5" />}
                {step.status === 'processing' && <Loader className="w-5 h-5 animate-spin text-whirlpool-cyan" />}
                {step.status === 'pending' && <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>}
              </div>
            </div>
            <div className="ml-4">
              <h3 className={`font-medium ${step.status === 'complete' ? 'text-white' :
                step.status === 'processing' ? 'text-whirlpool-cyan' : 'text-muted-foreground'
                }`}>
                {step.label}
                {index === 3 && isMulti && (
                  <span className="ml-2 text-xs inline-flex items-center text-whirlpool-cyan">
                    <Users className="h-3 w-3 mr-1" />
                    {destinationCount}
                  </span>
                )}
              </h3>

              {step.status === 'processing' && (
                <div className="mt-2 text-sm text-whirlpool-light animate-pulse">
                  Processing...
                </div>
              )}

              {step.status === 'complete' && (
                <div className="mt-2 text-sm text-green-400">
                  Complete
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
        <div
          className="h-2 bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue shadow-glow transition-all duration-700 ease-in-out"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>

      <div className="flex justify-between text-sm text-muted-foreground">
        <div>Start</div>
        <div className="text-whirlpool-cyan font-medium">{progressPercentage}%</div>
        <div>Complete</div>
      </div>

      <div className="mt-6 py-4 px-6 bg-secondary/50 backdrop-blur-md border border-whirlpool-cyan/30 rounded-lg overflow-hidden relative">
        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className="text-sm text-whirlpool-cyan font-medium flex items-center">
            <div className="mr-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="url(#paint0_linear)" strokeWidth="2" />
                <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="url(#paint1_linear)" strokeWidth="2" />
                <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="url(#paint2_linear)" />
                <defs>
                  <linearGradient id="paint0_linear" x1="2" y1="12" x2="22" y2="12" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#22D3EE" />
                    <stop offset="1" stopColor="#0EA5E9" />
                  </linearGradient>
                  <linearGradient id="paint1_linear" x1="6" y1="12" x2="18" y2="12" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#22D3EE" />
                    <stop offset="1" stopColor="#0EA5E9" />
                  </linearGradient>
                  <linearGradient id="paint2_linear" x1="10" y1="12" x2="14" y2="12" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#22D3EE" />
                    <stop offset="1" stopColor="#0EA5E9" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            WhirlPool Transaction Flow
          </div>
        </div>
        <div className="relative h-12 flex items-center justify-between">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-whirlpool-blue/10 to-transparent opacity-30"></div>
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-whirlpool-light/20 -translate-y-1/2"></div>

          <div className="flex items-center space-x-2 relative z-10">
            <div className="w-8 h-8 bg-gradient-to-br from-whirlpool-cyan to-whirlpool-blue rounded-full flex items-center justify-center text-xs shadow-glow">
              You
            </div>
            <ArrowRight className="text-whirlpool-light h-4 w-4" />
          </div>

          <div className="flex space-x-3 relative z-10">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-6 h-6 bg-secondary/70 backdrop-blur-sm border border-whirlpool-light/30 rounded-full flex items-center justify-center text-[10px] text-whirlpool-light shadow-sm"
              >
                {i}
              </div>
            ))}
          </div>

          <div className="flex items-center space-x-2 relative z-10">
            <ArrowRight className="text-whirlpool-light h-4 w-4" />
            <div className="w-8 h-8 bg-gradient-to-br from-whirlpool-blue to-whirlpool-light rounded-full flex items-center justify-center text-xs shadow-glow">
              {isMulti ? (
                <Users className="h-4 w-4" />
              ) : (
                "Dest"
              )}
            </div>
          </div>

          <div className="absolute top-1/2 left-0 -translate-y-1/2 h-3 w-3 bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue rounded-full animate-transaction-flow shadow-glow"></div>
        </div>
      </div>
    </div>
  );
};

export default TransactionProgress;
